import { ApiMatchSource, ApiMatchSourceType, ApiTransaction } from '../api';
import React, { Fragment, useState } from 'react';
import { getDisplayName, getDisplayValue, isChildField, orderBySchemaFields } from '../schema/helper';
import { Card, Col, Collapse, Row } from 'react-bootstrap';
import { createSearchParams, Link } from 'react-router';
import './TransactionAttributes.scss';
import ShowHideButton from '../common/ShowHideButton';
import { InternalApiAttributeType, InternalApiEntityType, InternalApiSearchOperator } from '../api-internal';
import { useQueryEntitySchema } from '../schema/entitySchemaActions';
import { SalvIconButton } from '@salvapp/ui-components';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { showFeedback } from '../feedback/feedbackReducer';
import { copyToClipboard } from '../common/clipboard';
import { useDispatch } from 'react-redux';
import { InternalNavigation } from '../navigation/helper';
import { SearchParamKeys } from '../common/searchParamKeys';
import { getHighlightedValue } from '../aml/sections/attributes/highlighters';

export interface TransactionAttributesProps {
  transaction: ApiTransaction;
  showLinkToTransactionPage?: boolean;
  showLinkToCustomerPage?: boolean;
  highlightedFields?: ApiMatchSource[];
  showId?: boolean;
}

export const TransactionAttributes = ({
  transaction,
  showLinkToTransactionPage = true,
  showLinkToCustomerPage = true,
  highlightedFields = [],
  showId = false,
}: TransactionAttributesProps) => {
  const dispatch = useDispatch();
  const { data: transactionSchema } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);
  const transactionSchemaFields = transactionSchema?.schemaFields.filter((schemaField) => !isChildField(schemaField));

  const [expanded, setExpanded] = useState(true);

  if (transaction === null) {
    return null;
  }
  const { attributes } = transaction;
  if (!attributes) {
    return null;
  }

  const relevantHightlightedFields = highlightedFields
    .filter((it) => it.type === ApiMatchSourceType.ORIGINAL)
    .map((it) => it.fieldKey)
    .join(' ')
    .trim();

  const orderedFieldNames = orderBySchemaFields(transactionSchemaFields, Object.keys(attributes));

  const getTransactionAsText = () => {
    let personAsText = '';

    for (const fieldName of orderedFieldNames) {
      const parentField = transactionSchemaFields?.find(
        (schemaField) => schemaField.fieldName === fieldName && !isChildField(schemaField)
      );
      personAsText = personAsText.concat(`${parentField?.displayName ?? fieldName}\t${attributes[fieldName]}\n`);
    }

    return personAsText;
  };

  function getIdRow() {
    return (
      <>
        <Row className='align-items-center'>
          <Col>
            <h5 className='mb-0'>ID</h5>
          </Col>
          <Col md='auto'>
            <span className='small'>{transaction.id}</span>
          </Col>
        </Row>
        <hr />
      </>
    );
  }

  function displayAttributeRow(
    fieldName: string,
    index: number,
    orderedFieldNames: string[],
    attributes: { [key: string]: string }
  ) {
    const schemaField = transactionSchemaFields?.find((schemaField) => schemaField.fieldName === fieldName);
    const parentSchemaFields = transactionSchemaFields?.filter((schemaField) => !schemaField.parentId);
    const parentSchemaField = parentSchemaFields?.find((schemaField) => schemaField.fieldName === fieldName);
    const formattedValue = getDisplayValue(schemaField, attributes[fieldName]);

    const Value = () =>
      relevantHightlightedFields.includes(fieldName)
        ? getHighlightedValue({ text: formattedValue, highlight: formattedValue })
        : formattedValue;

    return (
      <Fragment key={fieldName}>
        <Row className='align-items-center attribute-row'>
          <Col>
            <h5 className='mb-0'>{getDisplayName(schemaField) || fieldName}</h5>
          </Col>
          <Col md='auto' style={{ display: 'flex', gap: '4px', overflowWrap: 'anywhere' }}>
            <SalvIconButton
              ariaLabel={`Copy ${fieldName} value`}
              icon={faCopy}
              className={'copy-button'}
              onClick={async () => {
                const copied = await copyToClipboard(attributes[fieldName]);
                dispatch(
                  showFeedback(
                    copied
                      ? { message: 'Value copied', success: true }
                      : { message: 'Could not copy to clipboard', success: false }
                  )
                );
              }}
            />
            <span className='small'>
              {schemaField?.searchable ? (
                <Link
                  to={{
                    pathname: InternalNavigation.transactionSearch,
                    search: createSearchParams({
                      [SearchParamKeys.attributeIdentifier]: parentSchemaField!.id,
                      [SearchParamKeys.attributeType]: InternalApiAttributeType.SCHEMA_FIELD,
                      [SearchParamKeys.operator]: InternalApiSearchOperator.IS_EXACTLY,
                      [SearchParamKeys.query]: attributes[fieldName],
                    }).toString(),
                  }}
                >
                  <Value />
                </Link>
              ) : (
                <span>
                  <Value />
                </span>
              )}
            </span>
          </Col>
        </Row>
        {index < orderedFieldNames.length - 1 && <div className={'horizontal-separator-deprecated'} />}
      </Fragment>
    );
  }

  return (
    <Card className={'transaction-attributes'}>
      <Card.Header className='cursor-pointer' onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div className={'title-s'}>Transaction details</div>
          <SalvIconButton
            ariaLabel={`Copy transaction`}
            icon={faCopy}
            className={'copy-button'}
            onClick={async () => {
              const copied = await copyToClipboard(getTransactionAsText());
              dispatch(
                showFeedback(
                  copied
                    ? { message: 'Transaction copied', success: true }
                    : { message: 'Could not copy to clipboard', success: false }
                )
              );
            }}
          />
        </div>
        <ShowHideButton expanded={expanded} />
      </Card.Header>
      <Collapse in={expanded}>
        <Card.Body>
          {showId && getIdRow()}
          {orderedFieldNames.map((fieldName, i, a) => displayAttributeRow(fieldName, i, a, attributes))}
          {(showLinkToCustomerPage || showLinkToTransactionPage) && (
            <Fragment>
              <div className={'horizontal-separator-deprecated'} />
              <Row className='align-items-center attribute-row'>
                <Col>
                  {showLinkToCustomerPage && (
                    <Link to={`/customer/${transaction.personId}`}>See full customer info in Salv</Link>
                  )}
                  {showLinkToTransactionPage && (
                    <Link to={`/transactions/${transaction.id}`}>Go to transaction page</Link>
                  )}
                </Col>
              </Row>
            </Fragment>
          )}
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default TransactionAttributes;
